/**
 * The same record, as a proposal carries it.
 *
 * <p>A record in flight is `ProposalDataField[]` — one flat row per SUBFIELD, not one
 * row per field with subfields nested. `ProposalEdit` and every other proposal-side
 * caller hold that shape, and the shape question is identical: does tag X carry
 * subfield Y. Adapting here rather than writing a second matcher keeps one answer to
 * that question; two of them would be free to drift, and the one that drifted would
 * be the rarely-walked proposal path.
 *
 * <p>Rows with no `subfield_code` (a control field, an indicator-only row) contribute
 * a field with no subfields rather than being dropped, so a tag that is present but
 * carries nothing still reads as present — the predicate asks about a SUBFIELD, and
 * gets a truthful `false` either way.
 *
 * <p>Grouped by `(field_tag, field_index)`, not by tag alone. For a presence predicate
 * over a repeatable tag the two give the same answer, so this is not a bug being
 * avoided — it is that the backend's counterpart check (`subfield_groups`, evaluated
 * per field OCCURRENCE) is not occurrence-blind, and a helper that pools occurrences
 * would be the wrong thing to reach for the day a predicate needs the same precision.
 */
export var proposalDataToShapeFields = function (proposalData) {
    // A draft that has not been saved with any data yet has no shape to read, and "no
    // shape" is answered by the default form. `Array.isArray` rather than `?? []` only
    // because this is the first code to ITERATE the field — the check costs nothing and
    // keeps the iteration honest.
    //
    // It is deliberately NOT a guard against an unreadable payload. It cannot be one:
    // `ProposalForm` iterates the same field again for the MARC preview
    // (`proposalDataToRecordDataFields`), so a non-array would crash there whatever this
    // function returned. Nor does it need to be — `ProposalDetail.proposalData` is a
    // `List<ProposalDataField>`, so the API sends an array or null and nothing else.
    if (!Array.isArray(proposalData))
        return [];
    var fields = [];
    var byOccurrence = new Map();
    proposalData.forEach(function (row) {
        var tag = row.field_tag;
        if (!tag)
            return;
        var key = "".concat(tag, "#").concat(row.field_index);
        var field = byOccurrence.get(key);
        if (!field) {
            field = { fieldTag: tag, subfields: [] };
            byOccurrence.set(key, field);
            fields.push(field);
        }
        if (row.subfield_code)
            field.subfields.push({ subfieldCode: row.subfield_code });
    });
    return fields;
};
var holds = function (predicate, dataFields) { return dataFields.some(function (field) {
    var _a;
    return field.fieldTag === predicate.tag
        && ((_a = field.subfields) !== null && _a !== void 0 ? _a : []).some(function (sub) { return sub.subfieldCode === predicate.subfieldPresent; });
}); };
/**
 * The template a record should open in, or `undefined` for "the type's default".
 *
 * <p>`undefined` covers three cases that all mean the same thing to the caller —
 * there is no routing table, no entry matches, or the match IS the template already
 * loaded — and it matters that they collapse: `useFormConfig(type, undefined)` is the
 * unchanged default call, on the unchanged cache key, so the 598-record majority
 * costs exactly the one request it always cost.
 *
 * <p>Ambiguity resolves to the FIRST match in table order, and the order is the
 * server's precedence, not an accident of sorting: packaged templates come before
 * customer-created ones (a `copyFrom` copy inherits the source's predicate verbatim),
 * and only then by templateId. The ontology makes two matches impossible for 190 —
 * `subfield_groups` `exactlyOneOf [9, k]` is BLOCKING — but a client that renders
 * nothing when data contradicts the ontology would be reporting the ontology's
 * failure as its own.
 *
 * @param routing   the EFC's `templateRouting`, as the server published it
 * @param dataFields the record's MARC data fields
 * @param currentTemplateId the template the routing table came from (the EFC's own
 *   `templateId`), so a record already in its shape does not re-request it
 */
export var matchTemplateShape = function (routing, dataFields, currentTemplateId) {
    if (!(routing === null || routing === void 0 ? void 0 : routing.length) || !(dataFields === null || dataFields === void 0 ? void 0 : dataFields.length))
        return undefined;
    var match = routing.find(function (entry) { return entry.appliesWhen && holds(entry.appliesWhen, dataFields); });
    if (!match || match.templateId === currentTemplateId)
        return undefined;
    return match.templateId;
};
export default matchTemplateShape;
